import { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Building2, User } from "lucide-react";
import ReactApexChart from "react-apexcharts";
import {
    ApprovalGranularity,
    ApprovalKpis,
    ApprovalTypeStat,
    getApprovalKpis,
} from "../../../interfaces/enrichmentKpis";
import { CoverageTab, FunnelTab, NearCertainTab } from "./EnrichmentFunnelTabs";

type Tab = "approvals" | "funnel" | "coverage" | "nearCertain";
const TABS: Array<{ key: Tab; label: string }> = [
    { key: "approvals", label: "Approbations" },
    { key: "funnel", label: "Entonnoir" },
    { key: "coverage", label: "Couverture" },
    { key: "nearCertain", label: "Auto-approbation (échantillon)" },
];

// Validated pair (dataviz validator, light surface): blue = manual, orange = automatic.
const MANUAL_COLOR = "#2a78d6";
const AUTO_COLOR = "#eb6834";

const TYPE_LABELS: Record<string, string> = {
    "event:google_place_DFS": "Google Maps (DFS)",
    "event:google_place": "Google Maps",
    "event:website": "Site web",
    "event:website_content_OTS": "Email entreprise (site web)",
    "event:company_email": "Email entreprise",
    "event:owner_email": "Email dirigeant (trouvé sur le site)",
    "event:siren": "SIREN",
    "event:franchise": "Franchise",
    "event:company_linked": "Entreprise liée",
    "event:linkedin": "LinkedIn entreprise",
    "event:image": "Image",
    "event:website_crawl": "Lecture du site (crawl maison)",
    "event:osm_place": "OpenStreetMap",
    "event:datatourisme_place": "DATAtourisme",
    "person:linkedin": "LinkedIn dirigeant",
    "person:email": "Email perso dirigeant",
    "person:birthdate": "Date de naissance",
};

const typeLabel = (t: ApprovalTypeStat) => TYPE_LABELS[t.key] ?? t.type;

const PERIOD_CHOICES: Record<ApprovalGranularity, number[]> = {
    week: [8, 12, 26, 52],
    month: [6, 12, 24],
};

const fmt = (n: number) => n.toLocaleString("fr-FR");

function periodLabel(start: string, granularity: ApprovalGranularity): string {
    const d = new Date(start);
    return granularity === "week"
        ? d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
        : d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
}

const cellTotal = (c: { manual: number; auto: number }) => c.manual + c.auto;

// ─── Type card ────────────────────────────────────────────────────────────────
function TypeCard({
    stat,
    granularity,
    selected,
    onSelect,
}: {
    stat: ApprovalTypeStat;
    granularity: ApprovalGranularity;
    selected: boolean;
    onSelect: () => void;
}) {
    const n = stat.series.length;
    const current = n > 0 ? cellTotal(stat.series[n - 1]) : 0;
    const previous = n > 1 ? cellTotal(stat.series[n - 2]) : 0;
    const EntityIcon = stat.entity === "person" ? User : Building2;

    return (
        <button
            type="button"
            onClick={onSelect}
            className={`bg-white border rounded-xl p-3 flex flex-col gap-2 text-left transition-all ${
                selected ? "border-[#1A1A1A] shadow-sm" : "border-gray-200 hover:bg-[#F9F6F3]"
            }`}
        >
            <div className="flex items-center gap-1.5 text-gray-500">
                <EntityIcon className="w-3.5 h-3.5 shrink-0" />
                <span className="text-xs font-medium truncate">{typeLabel(stat)}</span>
            </div>
            <div>
                <p className="text-xl font-semibold leading-tight text-gray-900">{fmt(stat.total)}</p>
                <p className="text-xs text-gray-400 mt-0.5">approuvés dans la base</p>
            </div>
            <div className="border-t border-gray-100 pt-2 flex flex-col gap-1 text-xs">
                <div className="flex justify-between">
                    <span className="text-gray-500">{granularity === "week" ? "Semaine en cours" : "Mois en cours"}</span>
                    <span className="font-semibold text-gray-700">{fmt(current)}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-500">{granularity === "week" ? "Semaine dernière" : "Mois dernier"}</span>
                    <span className="font-semibold text-gray-700">{fmt(previous)}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-500">Manuel · auto</span>
                    <span className="text-gray-700">
                        {fmt(stat.manual)} · {fmt(stat.auto)}
                    </span>
                </div>
                {stat.imported > 0 && (
                    <div className="flex justify-between" title="Approuvés par une migration qui a recopié les données de l'établissement : comptés dans le total, absents de manuel / auto et des graphiques.">
                        <span className="text-gray-400">dont import</span>
                        <span className="text-gray-400">{fmt(stat.imported)}</span>
                    </div>
                )}
                {stat.recovered > 0 && (
                    <div className="flex justify-between" title="Date d'approbation perdue puis reconstituée (journal d'activité, données Google de l'établissement…) : présente dans les graphiques.">
                        <span className="text-gray-400">dont dates reconstituées</span>
                        <span className="text-gray-400">{fmt(stat.recovered)}</span>
                    </div>
                )}
                {stat.undated > 0 && (
                    <div className="flex justify-between" title="Approuvés sans date d'approbation (anciens imports / migrations) : comptés dans le total, absents des graphiques.">
                        <span className="text-gray-400">dont sans date</span>
                        <span className="text-gray-400">{fmt(stat.undated)}</span>
                    </div>
                )}
            </div>
        </button>
    );
}

// ─── Chart ────────────────────────────────────────────────────────────────────
function ApprovalChart({ stat, labels }: { stat: ApprovalTypeStat; labels: string[] }) {
    const manual = stat.series.map((c) => c.manual);
    const auto = stat.series.map((c) => c.auto);

    const options: ApexCharts.ApexOptions = {
        chart: {
            type: "bar",
            stacked: true,
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: "inherit",
            background: "transparent",
        },
        plotOptions: {
            bar: {
                columnWidth: labels.length > 26 ? "80%" : "55%",
                borderRadius: 4,
                borderRadiusApplication: "end",
                borderRadiusWhenStacked: "last",
            },
        },
        // White stroke = the 2px gap between the stacked segments.
        stroke: { show: true, width: 2, colors: ["#ffffff"] },
        colors: [MANUAL_COLOR, AUTO_COLOR],
        dataLabels: { enabled: false },
        xaxis: {
            categories: labels,
            labels: { style: { colors: "#6b7280", fontSize: "11px" }, rotate: -45, hideOverlappingLabels: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { colors: "#6b7280", fontSize: "11px" }, formatter: (v: number) => fmt(Math.round(v)) },
        },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3, xaxis: { lines: { show: false } } },
        tooltip: {
            theme: "light",
            shared: true,
            intersect: false,
            y: { formatter: (v: number) => fmt(v) },
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "left",
            fontSize: "12px",
            labels: { colors: "#374151" },
        },
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-900 mb-0.5">{typeLabel(stat)} — approuvés par période</p>
            <p className="text-xs text-gray-400 mb-3">
                Par date d'approbation · la dernière barre est la période en cours
                {stat.undated > 0 && ` · ${fmt(stat.undated)} approbations sans date ne figurent pas ici`}
                {stat.recovered > 0 && ` · ${fmt(stat.recovered)} dates reconstituées incluses`}
            </p>
            <ReactApexChart
                type="bar"
                height={300}
                width="100%"
                options={options}
                series={[
                    { name: "Manuel", data: manual },
                    { name: "Automatique", data: auto },
                ]}
            />
        </div>
    );
}

// ─── Table: every type × every period ────────────────────────────────────────
function ApprovalTable({
    types,
    labels,
    selectedKey,
    onSelect,
}: {
    types: ApprovalTypeStat[];
    labels: string[];
    selectedKey: string | null;
    onSelect: (key: string) => void;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);

    // Newest periods are on the right — start scrolled there.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }, [labels.length]);

    return (
        <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">Toutes les validations par période</p>
                <p className="text-xs text-gray-400">Manuel + automatique · survolez une case pour le détail · cliquez une ligne pour le graphique</p>
            </div>
            <div ref={scrollRef} className="overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead>
                        <tr className="text-gray-500">
                            <th className="sticky left-0 z-10 bg-white text-left font-medium px-4 py-2 whitespace-nowrap">Enrichissement</th>
                            {labels.map((l, i) => (
                                <th key={i} className="text-right font-medium px-2 py-2 whitespace-nowrap">
                                    {l}
                                    {i === labels.length - 1 && <span className="block text-[10px] font-normal text-gray-400">en cours</span>}
                                </th>
                            ))}
                            <th className="text-right font-medium px-4 py-2 whitespace-nowrap border-l border-gray-100">Sur la période</th>
                            <th className="text-right font-medium px-4 py-2 whitespace-nowrap">Total base</th>
                        </tr>
                    </thead>
                    <tbody>
                        {types.map((t) => {
                            const windowTotal = t.series.reduce((sum, c) => sum + cellTotal(c), 0);
                            const selected = t.key === selectedKey;
                            return (
                                <tr
                                    key={t.key}
                                    onClick={() => onSelect(t.key)}
                                    className={`border-t border-gray-100 cursor-pointer ${selected ? "bg-[#F5F0EB]" : "hover:bg-[#F9F6F3]"}`}
                                >
                                    <td className={`sticky left-0 z-10 px-4 py-2 whitespace-nowrap font-medium text-gray-800 ${selected ? "bg-[#F5F0EB]" : "bg-white"}`}>
                                        {typeLabel(t)}
                                    </td>
                                    {t.series.map((c, i) => {
                                        const v = cellTotal(c);
                                        return (
                                            <td
                                                key={i}
                                                title={`${labels[i]} — manuel ${fmt(c.manual)} · auto ${fmt(c.auto)}`}
                                                className={`text-right px-2 py-2 tabular-nums ${v === 0 ? "text-gray-300" : "text-gray-700"}`}
                                            >
                                                {fmt(v)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-right px-4 py-2 tabular-nums font-semibold text-gray-900 border-l border-gray-100">{fmt(windowTotal)}</td>
                                    <td className="text-right px-4 py-2 tabular-nums text-gray-700">{fmt(t.total)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function EnrichmentKpis() {
    const [granularity, setGranularity] = useState<ApprovalGranularity>("week");
    const [periods, setPeriods] = useState(12);
    const [data, setData] = useState<ApprovalKpis | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [tab, setTab] = useState<Tab>("approvals");
    // Weeks and months can be toggled faster than the aggregation answers: only the latest lands.
    const requestRef = useRef(0);

    const load = (g: ApprovalGranularity, p: number) => {
        const id = ++requestRef.current;
        setLoading(true);
        setError(null);
        getApprovalKpis(g, p)
            .then((d) => {
                if (id !== requestRef.current) return;
                setData(d);
                setSelectedKey((prev) =>
                    prev && d.types.some((t) => t.key === prev)
                        ? prev
                        : d.types.find((t) => t.key === "event:google_place_DFS")?.key ?? d.types[0]?.key ?? null
                );
            })
            .catch(() => id === requestRef.current && setError("Impossible de charger les KPI d'enrichissement."))
            .finally(() => id === requestRef.current && setLoading(false));
    };

    useEffect(() => {
        load(granularity, periods);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [granularity, periods]);

    const changeGranularity = (g: ApprovalGranularity) => {
        if (g === granularity) return;
        setGranularity(g);
        setPeriods(12);
    };

    const labels = useMemo(
        () => (data ? data.periods.map((p) => periodLabel(p.start, data.granularity)) : []),
        [data]
    );
    const selected = data?.types.find((t) => t.key === selectedKey) ?? null;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">KPI enrichissements</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Données approuvées, comptées à leur date d'approbation.
                            {data && (
                                <span className="text-gray-400">
                                    {" "}Base : {fmt(data.base.events)} événements · {fmt(data.base.people)} personnes · calculé à{" "}
                                    {new Date(data.generatedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                            )}
                        </p>
                    </div>
                    {tab === "approvals" && (
                    <div className="flex items-center gap-2">
                        <div className="flex rounded-sm border border-gray-200 bg-white overflow-hidden">
                            {(["week", "month"] as ApprovalGranularity[]).map((g) => (
                                <button
                                    key={g}
                                    onClick={() => changeGranularity(g)}
                                    className={`px-3 py-1.5 text-xs ${granularity === g ? "bg-black text-white" : "text-gray-600 hover:bg-gray-50"}`}
                                >
                                    {g === "week" ? "Semaines" : "Mois"}
                                </button>
                            ))}
                        </div>
                        <select
                            value={periods}
                            onChange={(e) => setPeriods(Number(e.target.value))}
                            className="rounded-sm border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
                        >
                            {PERIOD_CHOICES[granularity].map((n) => (
                                <option key={n} value={n}>
                                    {n} {granularity === "week" ? "semaines" : "mois"}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => load(granularity, periods)}
                            disabled={loading}
                            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 ml-1"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            Actualiser
                        </button>
                    </div>
                    )}
                </div>

                <div className="flex gap-1 mb-4 border-b border-gray-200">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === t.key ? "border-black text-gray-900 font-medium" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "funnel" && <FunnelTab />}
                {tab === "coverage" && <CoverageTab />}
                {tab === "nearCertain" && <NearCertainTab />}

                {tab === "approvals" && error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                {tab === "approvals" && loading && !data && <p className="text-sm text-gray-500">Chargement…</p>}

                {tab === "approvals" && data && data.types.length === 0 && (
                    <p className="text-sm text-gray-500">Aucun enrichissement approuvé dans la base.</p>
                )}

                {tab === "approvals" && data && data.types.length > 0 && (
                    <div className={`flex flex-col gap-6 transition-opacity ${loading ? "opacity-60" : ""}`}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            {data.types.map((t) => (
                                <TypeCard
                                    key={t.key}
                                    stat={t}
                                    granularity={data.granularity}
                                    selected={t.key === selectedKey}
                                    onSelect={() => setSelectedKey(t.key)}
                                />
                            ))}
                        </div>

                        {selected && <ApprovalChart stat={selected} labels={labels} />}

                        <ApprovalTable types={data.types} labels={labels} selectedKey={selectedKey} onSelect={setSelectedKey} />
                    </div>
                )}
            </div>
        </div>
    );
}
